import { useEffect, useState } from "react";
import { Users, CreditCard, ShieldCheck, GraduationCap, Building2, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import Seo from "@/components/Seo";

export default function Admin() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(false);
  const [apps, setApps] = useState([]);
  const [busy, setBusy] = useState(null);
  const [program, setProgram] = useState({});
  const [conts, setConts] = useState([]);
  const [contPick, setContPick] = useState({});

  const loadApps = () => api.get("/admin/applications").then((r) => setApps(r.data.applications || [])).catch(() => {});
  const loadConts = () => api.get("/admin/continuation-requests").then((r) => setConts(r.data.requests || [])).catch(() => {});

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => setError(true));
    loadApps();
    loadConts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reviewCont = async (id, decision) => {
    setBusy(id + decision);
    try {
      const body = decision === "approve"
        ? { decision, program_type: contPick[id] || "standard_pilot" }
        : { decision };
      await api.post(`/admin/continuation-requests/${id}/review`, body);
      toast.success(decision === "approve" ? "Continuation approved" : "Request declined");
      loadConts();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed."); }
    finally { setBusy(null); }
  };

  const act = async (id, action, body) => {
    setBusy(id + action);
    try {
      const r = await api.post(`/admin/applications/${id}/${action}`, body || {});
      toast.success(action === "approve" ? `Approved · org ${r.data.org_id}` : `Marked ${action}`);
      loadApps();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed."); }
    finally { setBusy(null); }
  };

  if (!user?.is_admin) {
    return (
      <AppLayout>
        <Seo title="Admin · FluentLoop" noindex />
        <div className="max-w-md mx-auto px-4 py-24 text-center">
          <ShieldCheck className="w-12 h-12 mx-auto" />
          <h1 className="font-cabinet font-extrabold text-2xl mt-4">Admin access required</h1>
          <p className="text-gray-600 mt-2">This area is restricted to administrators.</p>
        </div>
      </AppLayout>
    );
  }

  const cards = stats ? [
    { label: "Total users", value: stats.total_users, icon: Users, bg: "bg-mint" },
    { label: "Active subscribers", value: stats.active_subscribers, icon: CreditCard, bg: "bg-butter" },
    { label: "Organizations", value: stats.total_organizations, icon: Building2, bg: "bg-lavender" },
    { label: "Education orgs", value: stats.education_active_orgs, icon: GraduationCap, bg: "bg-sky" },
    { label: "Memberships", value: stats.total_memberships, icon: Users, bg: "bg-peach" },
    { label: "Consent records", value: stats.consent_records, icon: ShieldCheck, bg: "bg-mint" },
  ] : [];

  const pending = apps.filter((a) => ["pending_review", "needs_information"].includes(a.status));

  return (
    <AppLayout>
      <Seo title="Admin · FluentLoop" noindex />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <h1 className="font-cabinet font-extrabold text-3xl">Admin</h1>
        {error && <p className="text-coral mt-4">Could not load stats.</p>}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6">
          {cards.map((c) => (
            <div key={c.label} className={`nb-card ${c.bg} p-6`}>
              <c.icon className="w-7 h-7" />
              <div className="font-cabinet font-extrabold text-4xl mt-2">{c.value}</div>
              <div className="font-bold text-sm">{c.label}</div>
            </div>
          ))}
        </div>

        <h2 className="font-cabinet font-extrabold text-2xl mt-10 flex items-center gap-2"><GraduationCap className="w-6 h-6" /> Education applications</h2>
        <p className="text-sm text-gray-600">{pending.length} awaiting review · {apps.length} total</p>
        <Link to="/admin/education" className="nb-btn-outline py-2 px-4 text-sm inline-flex mt-2" data-testid="admin-open-education-admin">Open Education Admin →</Link>
        <div className="space-y-4 mt-4" data-testid="admin-applications">
          {apps.length === 0 && <p className="text-gray-500 text-sm">No applications yet.</p>}
          {apps.map((a) => (
            <div key={a.application_id} className="nb-card p-5" data-testid={`application-${a.application_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-cabinet font-bold text-lg">{a.institution_name} <span className="nb-tag bg-gray-100 text-gray-600 ml-1">{a.institution_type}</span></div>
                  <div className="text-sm text-gray-600 mt-1">{a.applicant_name || a.applicant_email} · {a.requested_role} · {a.country || "—"}</div>
                  <div className="text-xs text-gray-500 mt-1">{a.school_email || "no school email"} · domain: {a.claimed_email_domain || "—"} · ~{a.approx_student_count || "?"} learners</div>
                  <div className="text-xs mt-1">Method: <b>{a.verification_method}</b> · Status: <b data-testid="app-row-status">{a.status}</b>{a.matched_org_id ? ` · org ${a.matched_org_id}` : ""}</div>
                  {a.notes && <div className="text-xs text-gray-600 mt-1 italic">"{a.notes}"</div>}
                </div>
                {a.status !== "approved" && a.status !== "rejected" && (
                  <div className="flex flex-col gap-2">
                    <select value={program[a.application_id] || "standard_pilot"} onChange={(e) => setProgram((p) => ({ ...p, [a.application_id]: e.target.value }))} className="nb-input text-sm py-1.5" data-testid={`program-${a.application_id}`}>
                      <option value="standard_pilot">Standard pilot · 90 days</option>
                      <option value="extended_pilot">Extended pilot · 180 days</option>
                      <option value="founding_school">Founding school · 365 days</option>
                    </select>
                    <button onClick={() => act(a.application_id, "approve", { program_type: program[a.application_id] || "standard_pilot" })} disabled={busy} className="nb-btn-primary py-2 px-4 text-sm" data-testid={`approve-${a.application_id}`}>Approve pilot</button>
                    <button onClick={() => act(a.application_id, "request-info", { notes: "Please share proof of your school affiliation." })} disabled={busy} className="nb-btn-outline py-2 px-4 text-sm">Request info</button>
                    <button onClick={() => act(a.application_id, "reject", { notes: "Unable to verify institution." })} disabled={busy} className="nb-btn-outline py-2 px-4 text-sm text-coral border-coral">Reject</button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <h2 className="font-cabinet font-extrabold text-2xl mt-10 flex items-center gap-2"><CalendarClock className="w-6 h-6" /> Pilot continuation requests</h2>
        <p className="text-sm text-gray-600">{conts.filter((c) => c.status === "pending").length} pending · {conts.length} total</p>
        <div className="space-y-3 mt-4" data-testid="admin-continuations">
          {conts.length === 0 && <p className="text-gray-500 text-sm">No continuation requests yet.</p>}
          {conts.map((c) => (
            <div key={c.request_id} className="nb-card p-4" data-testid={`continuation-${c.request_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-cabinet font-bold">{c.org_name} <span className={`nb-tag ml-1 ${c.status === "pending" ? "bg-butter" : c.status === "approved" ? "bg-mint" : "bg-gray-100"}`} data-testid="continuation-status">{c.status}</span></div>
                  <div className="text-xs text-gray-500 mt-1">Current end: {c.current_pilot_end ? new Date(c.current_pilot_end).toLocaleDateString() : "—"} · requested {new Date(c.requested_at).toLocaleDateString()}{c.granted_end ? ` · granted until ${new Date(c.granted_end).toLocaleDateString()}` : ""}</div>
                  {c.note && <div className="text-xs text-gray-600 mt-1 italic">"{c.note}"</div>}
                </div>
                {c.status === "pending" && (
                  <div className="flex flex-col gap-2">
                    <select value={contPick[c.request_id] || "standard_pilot"} onChange={(e) => setContPick((p) => ({ ...p, [c.request_id]: e.target.value }))} className="nb-input text-sm py-1.5" data-testid={`cont-program-${c.request_id}`}>
                      <option value="standard_pilot">+90 days</option>
                      <option value="extended_pilot">+180 days (extended)</option>
                      <option value="founding_school">+365 days (founding)</option>
                    </select>
                    <button onClick={() => reviewCont(c.request_id, "approve")} disabled={busy} className="nb-btn-primary py-2 px-4 text-sm" data-testid={`cont-approve-${c.request_id}`}>Approve</button>
                    <button onClick={() => reviewCont(c.request_id, "decline")} disabled={busy} className="nb-btn-outline py-2 px-4 text-sm text-coral border-coral" data-testid={`cont-decline-${c.request_id}`}>Decline</button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </AppLayout>
  );
}
